import { supabase } from '../lib/supabase';

export const patientService = {
  /**
   * Search patients by name for autocomplete (doctor-specific)
   * @param {string} searchTerm - The search term to match patient names
   * @returns {Promise<Array>} Array of matching patients
   */
  async searchPatients(searchTerm) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('patients')?.select('*')?.eq('doctor_id', user?.id)?.ilike('patient_name', `%${searchTerm}%`)?.order('patient_name')?.limit(10);

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(patient => ({
        id: patient?.id,
        doctorId: patient?.doctor_id,
        patientName: patient?.patient_name,
        contactNumber: patient?.contact_number,
        email: patient?.email,
        dateOfBirth: patient?.date_of_birth,
        gender: patient?.gender,
        createdAt: patient?.created_at,
        updatedAt: patient?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error searching patients:', error);
      throw error;
    }
  },

  /**
   * Get a single patient by ID
   * @param {string} patientId - The patient ID
   * @returns {Promise<Object>} Patient data
   */
  async getPatient(patientId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('patients')?.select('*')?.eq('id', patientId)?.eq('doctor_id', user?.id)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        doctorId: data?.doctor_id,
        patientName: data?.patient_name,
        contactNumber: data?.contact_number,
        email: data?.email,
        dateOfBirth: data?.date_of_birth,
        gender: data?.gender,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error getting patient:', error);
      throw error;
    }
  },

  /**
   * Create a new patient
   * @param {Object} patientData - Patient information
   * @returns {Promise<Object>} Created patient data
   */
  async createPatient(patientData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const { data, error } = await supabase?.from('patients')?.insert({
          doctor_id: user?.id,
          patient_name: patientData?.patientName,
          contact_number: patientData?.contactNumber,
          email: patientData?.email || null,
          date_of_birth: patientData?.dateOfBirth || null,
          gender: patientData?.gender || null
        })?.select()?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        doctorId: data?.doctor_id,
        patientName: data?.patient_name,
        contactNumber: data?.contact_number,
        email: data?.email,
        dateOfBirth: data?.date_of_birth,
        gender: data?.gender,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  },

  /**
   * Update an existing patient
   * @param {string} patientId - The patient ID
   * @param {Object} patientData - Updated patient information
   * @returns {Promise<Object>} Updated patient data
   */
  async updatePatient(patientId, patientData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const updateData = {};
      if (patientData?.patientName) updateData.patient_name = patientData?.patientName;
      if (patientData?.contactNumber) updateData.contact_number = patientData?.contactNumber;
      if (patientData?.email !== undefined) updateData.email = patientData?.email;
      if (patientData?.dateOfBirth !== undefined) updateData.date_of_birth = patientData?.dateOfBirth;
      if (patientData?.gender !== undefined) updateData.gender = patientData?.gender;

      const { data, error } = await supabase?.from('patients')?.update(updateData)?.eq('id', patientId)?.eq('doctor_id', user?.id)?.select()?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        doctorId: data?.doctor_id,
        patientName: data?.patient_name,
        contactNumber: data?.contact_number,
        email: data?.email,
        dateOfBirth: data?.date_of_birth,
        gender: data?.gender,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating patient:', error);
      throw error;
    }
  },

  /**
   * Delete a patient
   * @param {string} patientId - The patient ID
   * @returns {Promise<void>}
   */
  async deletePatient(patientId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('patients')?.delete()?.eq('id', patientId)?.eq('doctor_id', user?.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting patient:', error);
      throw error;
    }
  }
};